/**
 * 驾车路径规划（H5 高德 JS API）
 * 对齐新项目 restapi 驾车规划：距离/时间用于底部 ETA。
 */

export var AMAP_ETA_ROUTE_STRATEGY = 0;

function toRad(d) {
  return (d * Math.PI) / 180;
}

export function haversineDistanceM(a, b) {
  var R = 6371000;
  var dLat = toRad(b.lat - a.lat);
  var dLng = toRad(b.lng - a.lng);
  var lat1 = toRad(a.lat);
  var lat2 = toRad(b.lat);
  var h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function estimateDurationByDistanceM(distanceM) {
  var speedMps = (30 * 1000) / 3600;
  return Math.max(60, Math.round(distanceM / speedMps));
}

export function formatDistanceKm(distanceM) {
  if (distanceM < 1000) {
    return Math.max(1, Math.round(distanceM)) + "米";
  }
  var km = distanceM / 1000;
  return (km < 10 ? km.toFixed(1) : Math.round(km)) + "公里";
}

export function formatDurationText(durationS) {
  var m = Math.max(1, Math.round(durationS / 60));
  if (m < 60) return m + "分钟";
  var h = Math.floor(m / 60);
  var mm = m % 60;
  return mm ? h + "小时" + mm + "分钟" : h + "小时";
}

function extractRoutePoints(route) {
  var points = [];
  var steps = (route && route.steps) || [];
  for (var i = 0; i < steps.length; i++) {
    var path = steps[i].path || [];
    for (var j = 0; j < path.length; j++) {
      var p = path[j];
      var lng = typeof p.lng === "function" ? p.lng() : p.lng;
      var lat = typeof p.lat === "function" ? p.lat() : p.lat;
      if (isFinite(lng) && isFinite(lat)) {
        points.push({ lng: lng, lat: lat });
      }
    }
  }
  return points;
}

function searchDrivingSegment(origin, destination, waypoints) {
  return new Promise(function (resolve) {
    if (!window.AMap || !window.AMap.Driving) {
      resolve({
        distanceM: 0,
        durationS: 0,
        points: [],
        error: "高德驾车插件未加载",
      });
      return;
    }
    var policy =
      window.AMap.DrivingPolicy && window.AMap.DrivingPolicy.REAL_TRAFFIC != null
        ? window.AMap.DrivingPolicy.REAL_TRAFFIC
        : AMAP_ETA_ROUTE_STRATEGY;
    var driving = new window.AMap.Driving({
      policy: policy,
      hideMarkers: true,
    });
    var start = new window.AMap.LngLat(origin.lng, origin.lat);
    var end = new window.AMap.LngLat(destination.lng, destination.lat);
    var opts = {};
    if (waypoints && waypoints.length) {
      opts.waypoints = waypoints.map(function (p) {
        return new window.AMap.LngLat(p.lng, p.lat);
      });
    }
    driving.search(start, end, opts, function (status, result) {
      var route = result && result.routes && result.routes[0];
      if (status === "complete" && route) {
        resolve({
          distanceM: Number(route.distance) || 0,
          durationS: Number(route.time) || 0,
          points: extractRoutePoints(route),
        });
        return;
      }
      resolve({
        distanceM: 0,
        durationS: 0,
        points: [],
        error: (result && result.info) || status || "道路规划失败",
      });
    });
  });
}

/**
 * 多站点沿道路规划。单次最多 16 个途经点，超出则分段拼接。
 */
export function fetchAmapDrivingRouteChain(points) {
  if (!points || points.length < 2) {
    return Promise.resolve({
      distanceM: 0,
      durationS: 0,
      points: [],
      error: "站点不足，无法规划线路",
    });
  }

  var merged = [];
  var distanceM = 0;
  var durationS = 0;
  var lastError = "";
  var CHUNK = 17;

  function runFrom(start) {
    if (start >= points.length - 1) {
      if (!distanceM && !durationS && !merged.length) {
        return Promise.resolve({
          distanceM: 0,
          durationS: 0,
          points: [],
          error: lastError || "道路规划失败",
        });
      }
      return Promise.resolve({
        distanceM: distanceM,
        durationS: durationS,
        points: merged,
      });
    }
    var end = Math.min(start + CHUNK, points.length - 1);
    var origin = points[start];
    var destination = points[end];
    var waypoints = points.slice(start + 1, end);
    return searchDrivingSegment(origin, destination, waypoints).then(function (
      result
    ) {
      if (
        (result && result.points && result.points.length) ||
        (result && result.distanceM && result.durationS)
      ) {
        if (result.points && result.points.length) {
          if (merged.length && result.points.length) {
            merged = merged.concat(result.points.slice(1));
          } else {
            merged = merged.concat(result.points);
          }
        }
        distanceM += result.distanceM || 0;
        durationS += result.durationS || 0;
      } else {
        lastError = (result && result.error) || "道路规划失败";
      }
      return runFrom(start + CHUNK);
    });
  }

  return runFrom(0);
}
